import React, { useState, useEffect } from 'react';
import { subcontractorApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus,
  Search,
  Phone,
  Mail,
  DollarSign,
  Pencil,
  Trash2,
  Wrench,
  Zap,
  Hammer,
  Droplets,
  Wind,
  PaintBucket,
  TreeDeciduous,
  HardHat,
} from 'lucide-react';
import { toast } from 'sonner';

const SUBCONTRACTOR_TYPES = [
  'Plumber', 'Electrician', 'Mason', 'HVAC', 'Roofer', 
  'Painter', 'Carpenter', 'Flooring', 'Landscaper', 'General Labor', 'Other'
];

const getTypeIcon = (type) => {
  switch (type) {
    case 'Plumber': return Droplets;
    case 'Electrician': return Zap;
    case 'Mason': return Hammer;
    case 'HVAC': return Wind;
    case 'Painter': return PaintBucket;
    case 'Landscaper': return TreeDeciduous;
    case 'Carpenter': return Hammer;
    default: return Wrench;
  }
};

const getTypeColor = (type) => {
  switch (type) {
    case 'Plumber': return 'bg-blue-500';
    case 'Electrician': return 'bg-yellow-500';
    case 'Mason': return 'bg-stone-500';
    case 'HVAC': return 'bg-cyan-500';
    case 'Roofer': return 'bg-red-600';
    case 'Painter': return 'bg-purple-500';
    case 'Carpenter': return 'bg-amber-600';
    case 'Flooring': return 'bg-orange-500';
    case 'Landscaper': return 'bg-green-600';
    default: return 'bg-slate-500';
  }
};

export default function Subcontractors() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    email: '',
    phone: '',
    type: 'Plumber',
    specialty: '',
    hourly_rate: '',
    notes: '',
  });

  useEffect(() => {
    fetchSubcontractors();
  }, []);

  const fetchSubcontractors = async () => {
    try {
      const response = await subcontractorApi.getAll();
      setSubs(response.data);
    } catch (error) {
      toast.error('Failed to load subcontractors');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (sub = null) => {
    if (sub) {
      setEditingSub(sub);
      setFormData({
        name: sub.name,
        company_name: sub.company_name || '',
        email: sub.email || '',
        phone: sub.phone || '',
        type: sub.type,
        specialty: sub.specialty || '',
        hourly_rate: sub.hourly_rate?.toString() || '',
        notes: sub.notes || '',
      });
    } else {
      setEditingSub(null);
      setFormData({
        name: '', company_name: '', email: '', phone: '',
        type: 'Plumber', specialty: '', hourly_rate: '', notes: '',
      });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    
    const payload = {
      ...formData,
      hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
    };

    try {
      if (editingSub) {
        await subcontractorApi.update(editingSub.id, payload);
        toast.success('Subcontractor updated');
      } else {
        await subcontractorApi.create(payload);
        toast.success('Subcontractor added');
      }
      setShowDialog(false);
      fetchSubcontractors();
    } catch (error) {
      toast.error('Failed to save subcontractor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this subcontractor?')) return;
    try {
      await subcontractorApi.delete(id);
      setSubs(subs.filter(s => s.id !== id));
      toast.success('Subcontractor deleted');
    } catch (error) {
      toast.error('Failed to delete subcontractor');
    }
  };

  const filteredSubs = subs.filter(sub => {
    const matchesSearch = sub.name.toLowerCase().includes(search.toLowerCase()) ||
      sub.company_name?.toLowerCase().includes(search.toLowerCase()) ||
      sub.email?.toLowerCase().includes(search.toLowerCase());
    const matchesType = typeFilter === 'all' || sub.type === typeFilter;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="subcontractors-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Subcontractors</h1>
          <p className="text-muted-foreground">Manage your network of trade professionals</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button onClick={() => handleOpenDialog()} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="add-sub-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Subcontractor
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingSub ? 'Edit Subcontractor' : 'Add Subcontractor'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Smith"
                    className="rounded-sm mt-1"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Company Name</Label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="Smith Plumbing LLC"
                    className="rounded-sm mt-1"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                    className="rounded-sm mt-1"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="rounded-sm mt-1"
                  />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(val) => setFormData({ ...formData, type: val })}
                  >
                    <SelectTrigger className="rounded-sm mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SUBCONTRACTOR_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Hourly Rate ($)</Label>
                  <Input
                    type="number"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                    placeholder="75"
                    className="rounded-sm mt-1"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Specialty</Label>
                  <Input
                    value={formData.specialty}
                    onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                    placeholder="Residential renovations, commercial work, etc."
                    className="rounded-sm mt-1"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes..."
                    className="rounded-sm mt-1"
                    rows={2}
                  />
                </div>
              </div>
              <Button onClick={handleSave} disabled={saving} className="w-full rounded-sm">
                {saving ? 'Saving...' : editingSub ? 'Update Subcontractor' : 'Add Subcontractor'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search subcontractors..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 rounded-sm"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[180px] rounded-sm">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {SUBCONTRACTOR_TYPES.map((type) => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Subcontractors Grid */}
      {filteredSubs.length === 0 ? (
        <Card className="rounded-sm">
          <CardContent className="py-16 text-center">
            <HardHat className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold mb-2">No subcontractors yet</h3>
            <p className="text-muted-foreground mb-4">
              Add plumbers, electricians, and other trade professionals
            </p>
            <Button onClick={() => handleOpenDialog()} className="rounded-sm">
              Add Your First Subcontractor
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSubs.map((sub) => {
            const TypeIcon = getTypeIcon(sub.type);
            return (
              <Card key={sub.id} className="rounded-sm hover:shadow-md transition-shadow" data-testid={`sub-card-${sub.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`h-12 w-12 rounded-sm ${getTypeColor(sub.type)} flex items-center justify-center text-white`}>
                        <TypeIcon className="h-6 w-6" strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="font-semibold">{sub.name}</p>
                        {sub.company_name && (
                          <p className="text-sm text-muted-foreground">{sub.company_name}</p>
                        )}
                      </div>
                    </div>
                    <Badge className={getTypeColor(sub.type)}>{sub.type}</Badge>
                  </div>

                  <div className="space-y-2 text-sm mb-4">
                    {sub.email && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Mail className="h-4 w-4" strokeWidth={1.5} />
                        <a href={`mailto:${sub.email}`} className="hover:text-orange-500">{sub.email}</a>
                      </div>
                    )}
                    {sub.phone && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="h-4 w-4" strokeWidth={1.5} />
                        <a href={`tel:${sub.phone}`} className="hover:text-orange-500">{sub.phone}</a>
                      </div>
                    )}
                    {sub.hourly_rate && (
                      <div className="flex items-center gap-2 text-green-700">
                        <DollarSign className="h-4 w-4" strokeWidth={1.5} />
                        ${sub.hourly_rate}/hour
                      </div>
                    )}
                    {sub.specialty && (
                      <p className="text-muted-foreground text-xs mt-2">{sub.specialty}</p>
                    )}
                  </div>

                  <div className="flex gap-2 pt-3 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 rounded-sm"
                      onClick={() => handleOpenDialog(sub)}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm text-red-600 hover:text-red-700"
                      onClick={() => handleDelete(sub.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
        {SUBCONTRACTOR_TYPES.slice(0, 6).map((type) => {
          const count = subs.filter(s => s.type === type).length;
          const TypeIcon = getTypeIcon(type);
          return (
            <Card key={type} className="rounded-sm">
              <CardContent className="p-4 text-center">
                <div className={`h-10 w-10 rounded-sm ${getTypeColor(type)} flex items-center justify-center text-white mx-auto mb-2`}>
                  <TypeIcon className="h-5 w-5" />
                </div>
                <p className="text-2xl font-bold">{count}</p>
                <p className="text-xs text-muted-foreground">{type}s</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
